import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  StackDivider,
  HStack,
  Button,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Badge,
  Switch
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from '@hooks/useDebounce';
import useDidMountEffect from '@hooks/useDidMount';
import useTickets from '@hooks/useTickets';
import SearchBox from '@components/SearchBox/SearchBox';
import FilterSelect from '@components/FiltersBox/FilterSelect';
import Pagination from '@components/Pagination';
import OutboundCallingTicketDetails from '@components/OutboundCallingTicketDetails';
import { updateOutboundCallingTicketsReqData } from '@store/actions/reqDataActions';
import {
  getTicketsByAgent,
  setCurrentTicket,
  clearCurrentTicket,
  getAgentStatus,
  updateAgentStatus
} from '@store/actions/outboundCallingActions';
import {
  getAgentStatusColor,
  getAgentStatusText,
  AGENT_STATUS_UPDATE_FORM_CONFIG,
  ticketsToEntity
} from '@views/OutboundCallingDashboard/utils';
import DynamicInputModal from '@components/DynamicInputModal';
import {
  AGENT_STATUS,
  OUTBOUND_CALLING_DASHBOARD_STRINGS,
  TAB_STATES,
  CALLBACK_REQUEST_STATUS_VALUES,
  TICKET_POLLING_INTERVAL_MS
} from '@views/OutboundCallingDashboard/constant';
import { ALERT_STATUS_TYPE, ROLES, SORT_ORDER } from '@constants';
import {
  formatDate,
  getDateOffsetByDaysFromReferenceDate,
  getLocalDate,
  formatLocalDateTime
} from '@utils/dateUtils';
import ModalLayout from '@components/Modal/ModalLayout';
import { setAlert } from '@store/actions/alertActions';
import { MdOfflineBolt, MdPhone } from 'react-icons/md';
import { FaDotCircle } from 'react-icons/fa';
import useAuthRole from '@hooks/useAuthRole';
import { AiOutlineEdit } from 'react-icons/ai';
import CopyableText from '@components/CopyableText';
import { VscCallOutgoing } from 'react-icons/vsc';
import InputComponent from '@components/Input';
import dayjs from 'dayjs';
import { OUTBOUND_CALLING_DEFAULT_OFFSET_DAY } from '@views/OutboundCallingDashboard/constant';

const AgentDashboard = () => {
  const { hasAnyAuthority } = useAuthRole();
  const { updateTicket: updateTicketViaHook } = useTickets();

  const dispatch = useDispatch();
  const dashboardStringConstants =
    OUTBOUND_CALLING_DASHBOARD_STRINGS.STRING_CONSTANTS[TAB_STATES.AGENT];

  // Redux state
  // Agent Id
  const { userId: agentId } = useSelector((state) => state?.auth?.user || {});

  // Request Data
  const reqData = useSelector(
    (state) => state.reqData.outboundCalling?.agentDashboard?.tickets || {}
  );

  // Config Data
  const {
    data: configData,
    isLoading: isConfigLoading,
    error: configError
  } = useSelector(
    (state) =>
      state.outboundCalling?.config || {
        data: null,
        isLoading: false,
        error: null
      }
  );

  const searches = configData?.agentDashboard?.searches || [];
  const filters = configData?.agentDashboard?.filters || [];
  const sortKey =
    configData?.agentDashboard?.sorting?.defaultSortKey || 'created_at';
  const sortOrder =
    configData?.agentDashboard?.sorting?.defaultsortOrder || SORT_ORDER.DESC;

  // Ticket Data
  const {
    data: tickets,
    isLoading: ticketsLoading,
    totalPages
  } = useSelector(
    (state) =>
      state.outboundCalling?.agentDashboard?.tickets || {
        data: [],
        isLoading: false,
        totalPages: 0
      }
  );

  const {
    status: agentStatus,
    isLoading: isAgentLoading,
    lastUpdatedAt: lastUpdated
  } = useSelector(
    (state) =>
      state.outboundCalling?.agentDashboard?.agent || {
        status: AGENT_STATUS.INACTIVE,
        isLoading: false,
        lastUpdatedAt: null
      }
  );

  const currentTicket = useSelector(
    (state) => state.outboundCalling?.currentTicket?.ticket
  );

  // Local state
  const {
    pageNumber = 0,
    searchKey = '',
    searchValue = '',
    filters: reqFilters = {},
    rangeKey = 'created_at',
    rangeValue = {}
  } = reqData;
  const [filterParams, setFilterParams] = useState(reqFilters || {});

  useEffect(() => {
    setFilterParams(reqData.filters || {});
  }, [reqData.filters]);

  const [searchParams, setSearchParams] = useState({
    searchKey: searchKey || '',
    searchValue: searchValue || ''
  });

  const [sortParams, setSortParams] = useState({
    sortKey,
    sortOrder
  });

  const [isModalLoading, setIsModalLoading] = useState(false);

  // Date range state with default last 7 days
  const [dateRange, setDateRange] = useState({
    start: rangeValue.min
      ? new Date(rangeValue.min)
      : null,
    end: rangeValue.max ? new Date(rangeValue.max) : new Date()
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);
  const debouncedSearchParams = useDebounce(searchParams, 500);
  const debouncedSortParams = useDebounce(sortParams, 500);
  const debouncedDateRangeParams = useDebounce(dateRange, 500);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isStatusUpdateModalOpen,
    onOpen: onStatusUpdateModalOpen,
    onClose: onStatusUpdateModalClose
  } = useDisclosure();

  /**
   * Fetch tickets for the agent
   */
  const fetchTickets = useCallback(() => {
    if (!agentId || !configData) {
      return;
    }
    // Build payload with filters, search, pagination, and date range
    const payload = {
      ...reqData,
      filters: debouncedFilterParams,
      searchKey: debouncedSearchParams.searchKey,
      searchValue: debouncedSearchParams.searchValue,
      rangeKey: 'created_at',
      rangeValue: {
        min: debouncedDateRangeParams.start
          ? formatLocalDateTime(debouncedDateRangeParams.start)
          : null,
        max: debouncedDateRangeParams.end
          ? formatLocalDateTime(debouncedDateRangeParams.end)
          : null
      }
    };

    // Fetch all tickets for this agent
    dispatch(
      getTicketsByAgent({
        agentId,
        payload
      })
    );
  }, [
    dispatch,
    agentId,
    configData,
    reqData,
    debouncedFilterParams,
    debouncedSearchParams,
    debouncedDateRangeParams
  ]);

  /**
   * Fetch agent status
   */
  const fetchAgentStatus = useCallback(() => {
    if (!agentId) {
      return;
    }
    dispatch(getAgentStatus({ agentId }));
  }, [dispatch, agentId]);

  /**
   * Initialize reqData with default searchKey from config
   */
  useEffect(() => {
    if (configData && searches.length > 0 && !reqData.searchKey) {
      const defaultSearch = searches.find((s) => s.isDefault);
      if (defaultSearch) {
        dispatch(
          updateOutboundCallingTicketsReqData({
            ...reqData,
            searchKey: defaultSearch.searchKey,
            searchValue: '',
            pageNumber: 0
          })
        );
      }
    }
  }, [configData, searches, reqData, dispatch]);

  /**
   * Update reqData with date range when debounced date range changes
   */
  useDidMountEffect(() => {
    dispatch(
      updateOutboundCallingTicketsReqData({
        ...reqData,
        rangeKey: 'created_at',
        rangeValue: {
          min: debouncedDateRangeParams.start
            ? formatLocalDateTime(debouncedDateRangeParams.start)
            : null,
          max: debouncedDateRangeParams.end
            ? formatLocalDateTime(debouncedDateRangeParams.end)
            : null
        },
        pageNumber: 0
      })
    );
  }, [debouncedDateRangeParams]);

  /**
   * Auto-fetch tickets when dependencies change
   */
  useDidMountEffect(() => {
    fetchTickets();
  }, [
    debouncedFilterParams,
    debouncedSearchParams,
    debouncedSortParams,
    debouncedDateRangeParams,
    pageNumber,
    dispatch
  ]);

  /**
   * Fetch agent status on mount and when agentId changes
   */
  useEffect(() => {
    fetchAgentStatus();
  }, [fetchAgentStatus]);

  /**
   * Polling mechanism to refresh tickets every 5 minutes
   * Only polls when agent status is ACTIVE to optimize API calls
   */
  useEffect(() => {
    // Only set up polling if agent is active
    if (agentStatus !== AGENT_STATUS.ACTIVE) {
      return;
    }

    // Set up interval to fetch tickets every 5 minutes
    const pollingInterval = setInterval(() => {
      fetchTickets();
    }, TICKET_POLLING_INTERVAL_MS);

    // Clean up interval on unmount or when dependencies change
    return () => {
      clearInterval(pollingInterval);
    };
  }, [agentStatus, fetchTickets]);

  /**
   * Handle filter change
   */
  const handleFilterChange = useCallback(
    ({ key, filters: filterValues }) => {
      setFilterParams((prev) => {
        const updatedParams = { ...prev };
        if (!filterValues || filterValues.length === 0) {
          delete updatedParams[key];
        } else {
          updatedParams[key] = filterValues;
        }
        return updatedParams;
      });

      dispatch(
        updateOutboundCallingTicketsReqData({
          ...reqData,
          filters: {
            ...reqData.filters,
            [key]: filterValues || []
          },
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  /**
   * Handle page change
   */
  const handlePageChange = useCallback(
    (page) => {
      const newPageNumber = Math.max(0, page - 1);
      dispatch(
        updateOutboundCallingTicketsReqData({
          ...reqData,
          pageNumber: newPageNumber
        })
      );
    },
    [dispatch, reqData]
  );

  /**
   * Handle Search Change
   * @type {(function(*): void)|*}
   */
  const handleSearchChange = useCallback(
    (search) => {
      const newSearchParams = {
        searchKey: search.searchKey || '',
        searchValue: search.searchValue || ''
      };

      setSearchParams(newSearchParams);

      dispatch(
        updateOutboundCallingTicketsReqData({
          ...reqData,
          searchKey: newSearchParams.searchKey,
          searchValue: newSearchParams.searchValue,
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  /**
   * Handle agent status update via modal
   */
  const handleStatusUpdate = useCallback(
    (formData) => {
      if (!agentId) {
        dispatch(
          setAlert({
            title: 'Unable to update status: User ID not found',
            status: ALERT_STATUS_TYPE.ERROR
          })
        );
        return;
      }

      setIsModalLoading(true);

      dispatch(
        updateAgentStatus({
          agentId,
          // NOTE :: this is not scalable as backend does not support the multiple status, use formData.isOnline if it support multiple status
          isOnline: formData.isOnline.toLowerCase() === 'true',
          onSuccess: () => {
            setIsModalLoading(false);
            onStatusUpdateModalClose();
            fetchAgentStatus();
          },
          onComplete: () => {
            setIsModalLoading(false);
          }
        })
      );
    },
    [dispatch, agentId, onStatusUpdateModalClose]
  );

  /**
   * Handle ticket click
   */
  const handleViewTicket = useCallback(
    (e, ticketData) => {
      e?.preventDefault();
      e?.stopPropagation();

      // ticketData could be either the ticket object directly (from action button)
      const ticket = findTicketByTicketId(ticketData);

      if (!ticket) {
        console.error('Ticket not found:', ticketData);
        dispatch(
          setAlert({
            title: 'Unable to open ticket: Ticket data not found',
            status: ALERT_STATUS_TYPE.ERROR
          })
        );
        return;
      }

      // Set the ticket in Redux state
      dispatch(setCurrentTicket(ticket));
      // Open the modal
      onOpen();
    },
    [tickets, dispatch, onOpen]
  );

  /**
   * Handle pre-call ticket status update to ON_CALL
   */
  const handlePreCallTicketUpdateAction = useCallback(
    async (e, ticketData) => {
      e?.preventDefault();
      e?.stopPropagation();

      // Find the ticket by ID
      const ticket = findTicketByTicketId(ticketData);

      if (!ticket) {
        dispatch(
          setAlert({
            title: 'Unable to update ticket: Ticket data not found',
            status: ALERT_STATUS_TYPE.ERROR
          })
        );
        return;
      }

      try {
        // Use unified update API to change status to ON_CALL
        await updateTicketViaHook(ticket.ticketId, {
          status: CALLBACK_REQUEST_STATUS_VALUES.ON_CALL
        });

        // Refresh tickets list after successful update
        fetchTickets();
      } catch (error) {
        console.error('Failed to update ticket status:', error);
      }
    },
    [tickets, updateTicketViaHook, fetchTickets, dispatch]
  );

  /**
   * Handle modal close
   */
  const handleCloseTicketModal = () => {
    // Clear the ticket from Redux
    dispatch(clearCurrentTicket());
    // Close the modal
    onClose();
  };

  /**
   * Get form config with current status pre-populated
   */
  const getStatusUpdateFormConfig = () => {
    // Map current status to isOnline boolean
    const currentIsOnline = agentStatus === AGENT_STATUS.ACTIVE;

    return AGENT_STATUS_UPDATE_FORM_CONFIG.map((field) => ({
      ...field,
      value: currentIsOnline
    }));
  };

  const findTicketByTicketId = (ticketData) => {
    if (ticketData?.type === 'ticket' && ticketData?.id) {
      return tickets.find((t) => t.ticketId === ticketData.id);
    }
    return ticketData;
  };

  /**
   * Handle date range change
   */
  const handleDateRangeDataChange = (e) => {
    if (e.target.name === 'startDate') {
      setDateRange((prevState) => ({
        ...prevState,
        start: !e.target.value ? null : new Date(e.target.value)
      }));
    } else if (e.target.name === 'endDate') {
      setDateRange((prevState) => ({
        ...prevState,
        end: !e.target.value ? null : new Date(e.target.value)
      }));
    }
  };

  if (isConfigLoading) {
    return (
      <Box h="100%" overflow="hidden">
        <VStack h="100%" justify="center" align="center">
          {renderConfigLoadingState()}
        </VStack>
      </Box>
    );
  }

  const hasValidConfig =
    configData &&
    (searches?.length > 0 ||
      filters?.length > 0 ||
      typeof filters === 'object');

  const getRowStyles = useCallback(
    () => ({
      boxShadow: 'none',
      backgroundColor: '#1c1c1c',
      border: '3px solid black',
      opacity: 1,
      transition: 'opacity 0.3s ease-in-out',
      filter: 'none',
      _hover: {
        backgroundColor: '#111111'
      }
    }),
    []
  );

  /**
   * Render loading state
   */
  const renderLoadingState = () => (
    <Flex justify="center" align="center" w="full" h="200px">
      <VStack spacing={3}>
        <Spinner thickness="4px" size="lg" color="colorPrimary" />
        <Text fontSize="sm" color="whiteAlpha.600">
          {dashboardStringConstants.LOADING_TICKETS}
        </Text>
      </VStack>
    </Flex>
  );

  const renderConfigLoadingState = () => (
    <Flex justify="center" align="center" w="full" h="200px">
      <VStack spacing={3}>
        <Spinner thickness="4px" size="lg" color="colorPrimary" />
        <Text fontSize="sm" color="whiteAlpha.600">
          Loading configuration...
        </Text>
      </VStack>
    </Flex>
  );

  /**
   * Render empty state
   */
  const renderEmptyState = () => (
    <Box
      p={8}
      bg="backGround"
      border="1px solid"
      borderColor="whiteAlpha.300"
      borderRadius="xl"
      textAlign="center"
    >
      <Text color="whiteAlpha.700" fontSize="lg">
        {dashboardStringConstants.NO_TICKETS_FOUND}
      </Text>
      <Text color="whiteAlpha.500" fontSize="sm" mt={2}>
        {dashboardStringConstants.NO_TICKETS_MESSAGE}
      </Text>
    </Box>
  );

  /**
   * Render tickets table
   */
  const renderTicketsTable = () => {
    const entityTickets = ticketsToEntity(tickets);

    if (!entityTickets.length) return null;

    return (
      <TableContainer
        border={'2px solid'}
        borderColor={'whiteAlpha.500'}
        borderRadius={'xl'}
        my={4}
        overflowX="auto"
        overflowY="visible"
        maxH="none"
      >
        <Table variant={'unstyled'}>
          <Thead>
            <Tr
              bg={'backGround'}
              borderBottom="2px solid"
              borderColor="whiteAlpha.500"
            >
              {entityTickets[0]?.data?.map((field, index) => (
                <Th
                  whiteSpace={'nowrap'}
                  key={field.name || `header-${index}`}
                  style={field?.position?.style || {}}
                  textAlign={'center'}
                  px={4}
                  py={3}
                >
                  <Text fontSize="sm" fontWeight="semibold">
                    {field?.title}
                  </Text>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {entityTickets.map((entityTicket) => (
              <Tr key={entityTicket?.id} {...getRowStyles()}>
                {entityTicket?.data?.map((field, fieldIndex) => (
                  <Td
                    key={field.name || `field-${fieldIndex}`}
                    whiteSpace={'nowrap'}
                    style={field?.position?.style || {}}
                    px={4}
                    py={3}
                  >
                    <HStack
                      justify={
                        field.showEditIcon || field.showViewIcon
                          ? 'space-between'
                          : 'space-evenly'
                      }
                      p={0}
                      transition="all 0.3s ease-in-out"
                      transform="scale(1)"
                    >
                      {field.type === 'switch' ? (
                        <Switch
                          isDisabled
                          size="sm"
                          defaultChecked={field.value}
                          colorScheme="brand"
                          color="white"
                        />
                      ) : field.type === 'icon' ? (
                        <Box color={field.color}>
                          {field.value ? (
                            <MdOfflineBolt size="1.2rem" />
                          ) : (
                            <FaDotCircle size="1.2rem" />
                          )}
                        </Box>
                      ) : field.type === 'badge' ? (
                        <Badge
                          colorScheme={field.color}
                          fontSize="sm"
                          px={3}
                          py={1}
                          borderRadius="xl"
                        >
                          {field.value}
                        </Badge>
                      ) : field.isCopyable ? (
                        <CopyableText
                          fontSize="sm"
                          title={field?.title || 'Text'}
                        >
                          {field?.maskValue || field?.value || '-'}
                        </CopyableText>
                      ) : (
                        <Text fontSize="sm">
                          {field?.maskValue || field?.value || '-'}
                        </Text>
                      )}
                      {/* Action Buttons - Edit and Phone */}
                      {hasAnyAuthority(ROLES.OUTBOUND_CALLING_AGENT) &&
                        field.showViewAndEditIcon && (
                          <HStack spacing={4}>
                            <Button
                              colorScheme={'brand'}
                              size="xs"
                              bg="black"
                              border={'1px solid'}
                              borderColor={'whiteAlpha.300'}
                              borderRadius={'lg'}
                              p={0}
                              onClick={(e) => handleViewTicket(e, entityTicket)}
                            >
                              <AiOutlineEdit />
                            </Button>
                            {/* Quick status update of a ticket -> make final status to ON_CALL */}
                            <Button
                              colorScheme="brand"
                              size="xs"
                              bg="black"
                              border="1px solid"
                              borderColor="whiteAlpha.300"
                              borderRadius="lg"
                              p={0}
                              onClick={(e) =>
                                handlePreCallTicketUpdateAction(e, entityTicket)
                              }
                              isDisabled={(() => {
                                const ticket = tickets.find(
                                  (t) => t.ticketId === entityTicket?.id
                                );
                                return (
                                  ticket?.status ===
                                  CALLBACK_REQUEST_STATUS_VALUES.ON_CALL
                                );
                              })()}
                              _disabled={{
                                opacity: 0.5,
                                cursor: 'not-allowed',
                                bg: 'gray.700',
                                borderColor: 'gray.500'
                              }}
                            >
                              {(() => {
                                const ticket = tickets.find(
                                  (t) => t.ticketId === entityTicket?.id
                                );
                                return ticket?.status ===
                                  CALLBACK_REQUEST_STATUS_VALUES.ON_CALL ? (
                                  <VscCallOutgoing size="1rem" />
                                ) : (
                                  <MdPhone size="1rem" />
                                );
                              })()}
                            </Button>
                          </HStack>
                        )}
                    </HStack>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  /**
   * Main content renderer
   */
  const renderContent = () => {
    if (ticketsLoading) return renderLoadingState();
    if (!tickets?.length) return renderEmptyState();
    return renderTicketsTable();
  };

  const renderModals = () => {
    return (
      <>
        {/* Agent Status Update Modal */}
        <ModalLayout
          isOpen={isStatusUpdateModalOpen}
          onClose={onStatusUpdateModalClose}
        >
          <DynamicInputModal
            title="Update Agent Status"
            onClose={onStatusUpdateModalClose}
            onSubmit={handleStatusUpdate}
            fields={getStatusUpdateFormConfig()}
            isLoading={isModalLoading}
          />
        </ModalLayout>
      </>
    );
  };

  return (
    <>
      {/* Modals */}
      {renderModals()}
      <VStack
        w={'100%'}
        alignItems="stretch"
        gap={4}
        px={4}
        pb={4}
        divider={<StackDivider borderColor="whiteAlpha.300" />}
      >
        {/* Tickets Count Header */}
        <Flex justify="space-between" align="center">
          <Heading size="md">My Tickets</Heading>
          {/*{!ticketsLoading && tickets?.length > 0 && (*/}
          {/*  <Text fontSize="sm" color="whiteAlpha.600">*/}
          {/*    {tickets.length} {dashboardStringConstants.TICKETS_FOUND}*/}
          {/*  </Text>*/}
          {/*)}*/}

          <VStack justify="space-between" align="center">
            <HStack justify="space-between" align="center" gap={2}>
              <Text ml={3} fontSize="sm">
                {dashboardStringConstants.AGENT_STATUS_LABEL}:
              </Text>
              <Text
                fontSize="sm"
                color={getAgentStatusColor(agentStatus)}
                fontWeight="bold"
              >
                {getAgentStatusText(agentStatus)}
              </Text>
              <Button
                size="xs"
                colorScheme="brand"
                variant="outline"
                onClick={onStatusUpdateModalOpen}
                isDisabled={isAgentLoading}
              >
                Change
              </Button>
            </HStack>
            {/* Last Updated */}
            {!!lastUpdated && (
              <Text fontSize="xs" color="whiteAlpha.600">
                {dashboardStringConstants.LAST_UPDATED}:{' '}
                {formatDate(lastUpdated)}
              </Text>
            )}
          </VStack>
        </Flex>

        {/*/!* Search & Filters *!/*/}
        {/* Search & Filters - Only show when config is available */}
        {hasValidConfig && (
          <VStack
            justifyContent="flex-start"
            alignItems="start"
            alignSelf="flex-start"
            gap={2}
          >
            {/* Search Box */}
            {Array.isArray(searches) && searches.length > 0 && (
              <SearchBox
                searches={searches}
                handleSearchChange={handleSearchChange}
              />
            )}
            {/* Date Range Picker */}
            <HStack alignItems="start" gap={2}>
              <VStack alignItems={'start'} gap={0}>
                <InputComponent
                  type="datetime-local"
                  value={getLocalDate({ date: dateRange?.start })}
                  max={getLocalDate({
                    date: dateRange?.end || dayjs().format('YYYY-MM-DDTHH:mm')
                  })}
                  name="startDate"
                  handleChange={handleDateRangeDataChange}
                  inputTitle="Start Date"
                  placeholder="DD/MM/YYYY HH:MM"
                  maxWidth="15rem"
                  size="md"
                />
              </VStack>
              <VStack alignItems={'start'} gap={0}>
                <InputComponent
                  type="datetime-local"
                  value={getLocalDate({ date: dateRange.end })}
                  min={getLocalDate({ date: dateRange?.start })}
                  max={getLocalDate({
                    date: dayjs().format('YYYY-MM-DDTHH:mm')
                  })}
                  name="endDate"
                  handleChange={handleDateRangeDataChange}
                  inputTitle="End Date"
                  placeholder="DD/MM/YYYY HH:MM"
                  maxWidth="15rem"
                  size="md"
                />
              </VStack>
            </HStack>
            {((Array.isArray(filters) && filters.length > 0) ||
              (typeof filters === 'object' &&
                filters !== null &&
                Object.keys(filters).length > 0)) && (
              <FilterSelect
                handleFilterSubmit={handleFilterChange}
                filtersData={filters}
                defaultFiltersData={filterParams}
              />
            )}
          </VStack>
        )}

        {/* Pagination */}
        {!ticketsLoading && totalPages > 1 && (
          <Pagination
            onPageChange={handlePageChange}
            currentPage={pageNumber + 1}
            totalPages={totalPages}
          />
        )}

        {/* Content */}
        {renderContent()}
      </VStack>

      {/* Ticket Modal */}
      {currentTicket && (
        <ModalLayout
          isOpen={isOpen}
          onClose={handleCloseTicketModal}
          size={'3xl'}
          scrollBehavior={'inside'}
        >
          <OutboundCallingTicketDetails
            isOpen={isOpen || !!currentTicket}
            onClose={handleCloseTicketModal}
            onTicketUpdate={fetchTickets}
          />
        </ModalLayout>
      )}
    </>
  );
};

export default AgentDashboard;
